<template>
  <div class="sc-container" @scroll="handleScroll">
    <div :class="['sticky-header', { 'shadow': isScrolled }]">
      <div class="row align-items-end">
        <div class="col-auto sticky-header__info">
          <div v-if="timer_enabled" class="gm-timer-compact">
            <div class="gm-timer-compact__display" :class="{ 'gm-timer-compact__display--running': timerEnabled }">
              <span class="gm-timer-compact__label">Event</span>
              <span class="gm-timer-compact__time">{{hours}}:{{minutes}}:{{seconds}}</span>
            </div>
            <div class="gm-timer-compact__controls">
              <button class="gm-timer-compact__toggle" @click="startTimer()">
                <i class="tim-icons icon-triangle-right-17"></i>
              </button>
              <button class="gm-timer-compact__toggle" @click="stopTimer()">
                <i class="tim-icons icon-button-pause"></i>
              </button>
              <button class="gm-timer-compact__toggle" @click="resetTimer()">
                <i class="tim-icons icon-refresh-01"></i>
              </button>
            </div>
          </div>
          <pacing-strip v-if="pacing"
                        :status="pacing.status"
                        :projected-finish="pacing.projectedFinish"
                        :projected-delta="pacing.projectedDelta"
                        :sample-size="pacingBaseline.sampleSize"
                        :enough-history="pacingBaseline.enoughHistory"
                        :min-sample="MIN_SAMPLE"
                        :timer-paused="timer_enabled && !timerEnabled && totalSeconds !== originalTotalSeconds" />
        </div>
        <div class="col text-right sticky-header__actions">
          <base-button
            @click.native="checkMissingData()"
            class="utility-btn mr-2"
            link
          >
            <i class="tim-icons icon-zoom-split mr-1"></i>
            <span class="d-none d-sm-inline">Check data</span>
          </base-button>
          <base-button
            @click.native="toggleAllPuzzles()"
            class="utility-btn"
            link
          >
            <i class="tim-icons mr-1" :class="allExpanded ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
            <span class="d-none d-sm-inline">{{ allExpanded ? 'Collapse all' : 'Expand all' }}</span>
          </base-button>
        </div>
      </div>
    </div>
    <transition-group name="puzzle-list" tag="div" class="sc-content">
      <div v-for="entry in displayPuzzles" :key="'P'+entry.puzzle.puzzle_id"
           :data-flat-index="entry.flatIndex"
           class="puzzle-accordion-item"
           :class="{
             'puzzle-accordion-item--overdue': pacingChip(entry.puzzle) && pacingChip(entry.puzzle).band === 'overdue',
             'puzzle-accordion-item--child': entry.isChild,
             'puzzle-accordion-item--open': entry.puzzle.accordionOpen,
             'puzzle-accordion-item--solved': entry.puzzle.solve_time && !entry.puzzle.did_not_finish,
             'puzzle-accordion-item--dnf': entry.puzzle.did_not_finish
           }">
        <!-- Accordion header -->
        <div class="puzzle-accordion-header" @click="togglePuzzle(entry.flatIndex)">
          <div class="puzzle-accordion-header__left">
            <span class="puzzle-number">{{ entry.label }}</span>
            <span class="puzzle-name">{{ entry.puzzle.name }}</span>
          </div>
          <div class="puzzle-accordion-header__right">
            <span v-if="pacingChip(entry.puzzle)"
                  class="puzzle-badge"
                  :class="'puzzle-badge--pace-' + pacingChip(entry.puzzle).band">
              <i v-if="pacingChip(entry.puzzle).band === 'overdue'" class="tim-icons icon-alert-circle-exc mr-1"></i>
              <i v-else-if="pacingChip(entry.puzzle).band === 'approaching'" class="tim-icons icon-watch-time mr-1"></i>
              {{ pacingChipLabel(pacingChip(entry.puzzle)) }}
            </span>
            <span v-if="parseInt(entry.puzzle.hints_given_count) > 0" class="puzzle-badge puzzle-badge--hints">
              {{ entry.puzzle.hints_given_count }} interaction{{ parseInt(entry.puzzle.hints_given_count) !== 1 ? 's' : '' }}
            </span>
            <span v-if="entry.puzzle.breakage_occurred" class="puzzle-badge puzzle-badge--breakage">Issues</span>
            <span v-if="enableInRoomDisplay && entry.puzzle.hintsSentThisSession > 0" class="puzzle-badge puzzle-badge--sent">
              {{ entry.puzzle.hintsSentThisSession }} sent
            </span>
            <div class="puzzle-quick-actions" @click.stop>
              <button class="puzzle-quick-btn puzzle-quick-btn--complete"
                      :class="{ active: entry.puzzle.solve_time && !entry.puzzle.did_not_finish }"
                      @click="quickComplete(entry.puzzle)">
                <i class="tim-icons icon-check-2"></i>
                <span class="puzzle-quick-btn__label">{{ entry.puzzle.solve_time && !entry.puzzle.did_not_finish ? 'Finished' + (entry.puzzle.solve_time_label ? ' at ' + entry.puzzle.solve_time_label : '') : 'Complete' }}</span>
              </button>
              <button class="puzzle-quick-btn puzzle-quick-btn--dnf"
                      :class="{ active: entry.puzzle.did_not_finish }"
                      @click="quickDnf(entry.puzzle)">
                <i class="tim-icons icon-simple-remove"></i>
                <span class="puzzle-quick-btn__label">Did not finish</span>
              </button>
              <button v-if="enableInRoomDisplay"
                      class="puzzle-quick-btn puzzle-quick-btn--send"
                      :class="{ active: entry.puzzle.hintPanelOpen }"
                      @click="toggleHintPanel(entry.flatIndex)">
                <i class="tim-icons icon-send"></i>
                <span class="puzzle-quick-btn__label">Send interaction</span>
              </button>
              <button class="puzzle-quick-btn puzzle-quick-btn--expand"
                      @click="togglePuzzle(entry.flatIndex)">
                <i class="tim-icons" :class="entry.puzzle.accordionOpen ? 'icon-minimal-up' : 'icon-minimal-down'"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Hint panel (quick action) -->
        <div v-if="enableInRoomDisplay && entry.puzzle.hintPanelOpen" class="puzzle-hint-quick-section">
          <puzzle-hint-panel
            :puzzle-id="entry.puzzle.source_puzzle_id"
            :puzzle-name="entry.puzzle.name"
            :game-id="gameId"
            :expanded.sync="entry.puzzle.hintPanelOpen"
            :hints-sent-count="entry.puzzle.hintsSentThisSession || 0"
            @send-hint="handlePuzzleHintSent(entry.puzzle, $event)"
          />
        </div>

        <!-- Accordion body -->
        <div v-show="entry.puzzle.accordionOpen" class="puzzle-accordion-body">
          <!-- Info strip -->
          <div v-if="entry.puzzle.description || entry.puzzle.solution || entry.puzzle.solution_img" class="puzzle-info-strip">
            <img v-if="entry.puzzle.solution_img"
                 class="puzzle-info-strip__img"
                 :src="entry.puzzle.solution_img"
                 alt="Solution"
                 @click="expandImage(entry.puzzle.solution_img)" />
            <div class="puzzle-info-strip__text">
              <p v-if="entry.puzzle.description" class="puzzle-info-strip__line">
                <span class="puzzle-info-strip__label">Description</span>
                {{ entry.puzzle.description }}
              </p>
              <p v-if="entry.puzzle.solution" class="puzzle-info-strip__line">
                <span class="puzzle-info-strip__label">Goal</span>
                {{ entry.puzzle.solution }}
              </p>
            </div>
          </div>

          <!-- Fields -->
          <div class="puzzle-fields-row">
            <!-- Completion time -->
            <div class="puzzle-field-group puzzle-field-group--time">
              <label class="puzzle-field-label">Completion time</label>
              <div class="puzzle-field--time">
                <div class="puzzle-time-group" :class="{ 'is-disabled': entry.puzzle.did_not_finish }">
                  <base-input class="mb-0 puzzle-time-picker">
                    <el-time-picker :value="entry.puzzle.solve_time"
                      v-on:input="onSolveTimeInput(entry.puzzle, $event)" v-on:change="setDraft()"
                      :format="picker_time_format" placeholder="HH:MM"
                      :disabled="entry.puzzle.did_not_finish"
                    ></el-time-picker>
                  </base-input>
                  <div class="puzzle-now-append gt-now-append"
                       @click="!entry.puzzle.did_not_finish && setNow(entry.puzzle)">
                    Now
                  </div>
                </div>
                <label v-if="entry.puzzle.error" class="error puzzle-time-error">{{ entry.puzzle.error }}</label>
              </div>
              <div class="puzzle-dnf-toggle">
                <el-switch active-color="#e14eca"
                           v-model="entry.puzzle.did_not_finish"
                           @change="changeValidation(entry.puzzle)" />
                <label class="puzzle-dnf-label">Did not finish</label>
              </div>
            </div>

            <!-- Interactions -->
            <div class="puzzle-field-group puzzle-field-group--interactions">
              <label class="puzzle-field-label">Interactions</label>
              <div class="puzzle-field--hint-count">
                <base-input class="mb-0">
                  <el-input-number v-model="entry.puzzle.hints_given_count"
                    :min="0" :step="1" size="small"
                    @change="setDraft()">
                  </el-input-number>
                </base-input>
              </div>
              <div class="puzzle-field--desc">
                <label class="puzzle-field-label">Description</label>
                <textarea class="form-control puzzle-textarea" placeholder="Describe interactions..."
                  v-on:change="setDraft()" v-model="entry.puzzle.hints_given_description" rows="2"></textarea>
              </div>
            </div>

            <!-- Issues -->
            <div class="puzzle-field-group puzzle-field-group--issues">
              <label class="puzzle-field-label">Issues</label>
              <div class="puzzle-field--breakage-check">
                <el-switch active-color="#e14eca"
                           v-model="entry.puzzle.breakage_occurred"
                           @change="setDraft()" />
                <label class="puzzle-dnf-label">Issue occurred</label>
              </div>
              <div v-if="entry.puzzle.breakage_occurred" class="puzzle-field--desc">
                <label class="puzzle-field-label">Description</label>
                <textarea class="form-control puzzle-textarea" placeholder="Describe the issue..."
                  v-on:change="setDraft()" v-model="entry.puzzle.breakage_description" rows="2"></textarea>
              </div>
            </div>
          </div>

        </div>
      </div>
    </transition-group>

    <modal :show.sync="modals.image"
           body-classes="p-0"
           modal-classes="modal-dialog-centered">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">
        <img slot="image" style="width: 100%" :src="selectedImage"  alt="Card image cap"/>
      </card>
    </modal>
  </div>
</template>

<script>
import {TimeSelect, Popover, TimePicker, Switch, InputNumber} from 'element-ui';
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {Auth} from "aws-amplify";
import swal from "sweetalert2";
import PuzzleHintPanel from "./PuzzleHintPanel";
import PacingStrip from "./PacingStrip.vue";
import { buildBaseline, evenSpreadBaseline, computePacing, formatDelta, formatDuration, MIN_SAMPLE, TICK_MS } from "@/util/trackerPacing";
import { mergePickedSolveTime } from "@/util/trackerSolveTime";

export default {
  name: "PuzzleStep",
  components: {
    [TimeSelect.name]: TimeSelect,
    [Popover.name]: Popover,
    [TimePicker.name]: TimePicker,
    [Switch.name]: Switch,
    [InputNumber.name]: InputNumber,
    PuzzleHintPanel,
    PacingStrip
  },
  props: {
    gameInstanceId: { type: [String, Number], default: null },
    enableInRoomDisplay: { type: Boolean, default: false },
    showCompleteButton: { type: Boolean, default: true },
    socket: { default: null },
    gameConfig: { type: Object, default: null },
    gameId: { type: [Number, String], default: null },
    displaySettings: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      picker_time_format: 'hh:mm A',
      allExpanded: false,
      model: {
        puzzles: [],
      },
      hours: '00',
      seconds: '30',
      minutes: '210',
      originalTotalMinutes: 0,
      lastTime: null, // Stores the last timestamp
      timerId: null, // Used to track requestAnimationFrame
      totalSeconds: 66,
      timer_enabled: false,
      timer_auto_start: false,
      originalTotalSeconds: 0,
      countUpMode: false,
      timerEnabled: false,
      timerSpeed: 1,
      countdown_mode: 'up',
      selectedImage: '',
      pacingRows: null,
      pacingBaseline: null,
      pacingStart: null,
      pacingLimitSeconds: 0,
      pacingNow: Date.now(),
      pacingTimerId: null,
      MIN_SAMPLE: MIN_SAMPLE,
      isScrolled: false,
      hasScrollableContent: false,
      modals: {
        image: false
      }
    }
  },
  watch: {
    timerEnabled(newVal) {
      if (newVal) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    },
    'model.puzzles': {
      handler() {
        this.$nextTick(() => {
          this.checkScrollableContent();
        });
      },
      deep: true
    }
  },
  computed: {
    pacingEnabled() {
      return Boolean(this.displaySettings && this.displaySettings.pacing_enabled)
    },
    pacing() {
      if (!this.pacingEnabled || !this.pacingBaseline || !this.pacingStart) {
        return null
      }
      return computePacing({
        baseline: this.pacingBaseline,
        puzzles: this.model.puzzles,
        startDate: this.pacingStart,
        now: new Date(this.pacingNow),
        limitSeconds: this.pacingLimitSeconds
      })
    },
    displayPuzzles() {
      const result = []
      const isDone = (p) => {
        // While expanded, freeze sort position to prevent jumping
        if (p.accordionOpen && p.sortAsDone !== undefined) return p.sortAsDone
        return (p.solve_time && !p.did_not_finish) || p.did_not_finish
      }

      // Build original position-based labels before sorting by completion
      const topLevelByPosition = this.model.puzzles
        .filter(p => !p.parent_id)
        .slice()
        .sort((a, b) => a.position - b.position)

      const parentLabelMap = new Map()
      topLevelByPosition.forEach((parent, i) => {
        parentLabelMap.set(parent.puzzle_id, i + 1)
      })

      const childLabelMap = new Map()
      topLevelByPosition.forEach(parent => {
        const childrenByPosition = this.model.puzzles
          .filter(c => c.parent_id && Number(c.parent_id) === Number(parent.source_puzzle_id))
          .slice()
          .sort((a, b) => a.position - b.position)
        childrenByPosition.forEach((child, ci) => {
          childLabelMap.set(child.puzzle_id, parentLabelMap.get(parent.puzzle_id) + String.fromCharCode(97 + ci))
        })
      })

      // Now sort by completion status for display order
      const topLevel = topLevelByPosition.slice().sort((a, b) => {
        const aDone = isDone(a) ? 1 : 0
        const bDone = isDone(b) ? 1 : 0
        if (aDone !== bDone) return aDone - bDone
        return a.position - b.position
      })

      topLevel.forEach(parent => {
        const flatIndex = this.model.puzzles.indexOf(parent)
        result.push({ puzzle: parent, flatIndex, label: String(parentLabelMap.get(parent.puzzle_id)), isChild: false })

        const children = this.model.puzzles
          .filter(c => c.parent_id && Number(c.parent_id) === Number(parent.source_puzzle_id))
          .sort((a, b) => {
            const aDone = isDone(a) ? 1 : 0
            const bDone = isDone(b) ? 1 : 0
            if (aDone !== bDone) return aDone - bDone
            return a.position - b.position
          })
        children.forEach(child => {
          const childFlatIndex = this.model.puzzles.indexOf(child)
          result.push({
            puzzle: child,
            flatIndex: childFlatIndex,
            label: childLabelMap.get(child.puzzle_id),
            isChild: true
          })
        })
      })
      return result
    },
    sortedIntervals() {
      let intervals = this.displaySettings.time_adjustment_intervals || [1, 3, 5]
      if (typeof intervals === 'string') { intervals = JSON.parse(intervals) }
      return intervals.slice().sort((a, b) => a - b)
    }
  },
  methods: {
    resetState() {
      this.model.puzzles = []
      this.hours = '00'
      this.seconds = '30'
      this.minutes = '210'
      this.timer_enabled = false
      this.timerEnabled = false
    },
    draftKey(key) {
      if (!this.gameInstanceId) {
        console.warn('draftKey called with no gameInstanceId for key:', key)
        return key + '_NONE'
      }
      return key + '_' + this.gameInstanceId
    },
    formatSolveTime(solveTime) {
      if (!solveTime || !(solveTime instanceof Date)) return ''
      if (this.time_format === 'HH:mm') {
        return solveTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      }
      return solveTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
    },
    checkScrollableContent() {
      const container = this.$el;
      if (container) {
        this.hasScrollableContent = container.scrollHeight > container.clientHeight;
      }
    },
    setTimerSettings(settings, timer_value, minutes) {
      const countUpMode = settings.countdown_mode === 'up' ? true : false
      this.originalTotalMinutes = minutes;
      this.timer_enabled = settings.timer_enabled;
      this.timer_auto_start = settings.timer_auto_start;
      this.countdown_mode = settings.countdown_mode;
      this.countUpMode = countUpMode; // Set mode (true = count up, false = count down)

      let isNegative = false;

      if (countUpMode) {
        // Start at 0 and count UP instead of down
        this.totalSeconds = 0;
      } else {
        if (timer_value) {
          // Check if input has a negative sign
          if (timer_value.startsWith("-")) {
            isNegative = true;
            timer_value = timer_value.substring(1); // Remove negative sign for parsing
          }

          let [h, m, s] = timer_value.split(":").map(num => parseInt(num, 10) || 0);

          // Convert to total seconds
          this.totalSeconds = (h * 3600) + (m * 60) + s;

          // Reapply the negative sign if necessary
          if (isNegative) {
            this.totalSeconds = -this.totalSeconds;
          }
        } else {
          this.totalSeconds = minutes * 60; // Default initialization
        }
      }

      // Ensure UI displays correct time
      this.updateTimeDisplay();
      this.originalTotalSeconds = this.totalSeconds;
      this.lastTime = performance.now();
      if(settings.timer_auto_start){
        this.timerEnabled = true;
      }
    },
    startTimer() {
      if (this.timerId) return; // Prevent multiple timers

      this.timerEnabled = true; // Ensure timer state is active
      this.lastTime = performance.now();
      this.timerId = requestAnimationFrame(this.updateTimer);
    },
    updateTimer() {
      if (!this.timerEnabled) return;

      let now = performance.now();
      let elapsedTime = Math.floor((now - this.lastTime) / 1000);

      if (elapsedTime !== 0) {
        if (this.countUpMode) {
          this.totalSeconds += elapsedTime;
        } else {
          this.totalSeconds -= elapsedTime;
        }

        this.lastTime = now;
        this.updateTimeDisplay();
      }

      this.timerId = requestAnimationFrame(this.updateTimer);
    },
    stopTimer() {
      if (this.timerId) {
        cancelAnimationFrame(this.timerId);
        this.timerId = null;
      }
      this.timerEnabled = false;
    },
    resetTimer() {
      this.stopTimer();
      if (this.countUpMode) {
        this.totalSeconds = 0;
      } else {
        this.totalSeconds = this.originalTotalMinutes * 60;
      }
      this.updateTimeDisplay();
      this.timerEnabled = false;

      if (this.timerSpeed !== 1) {
        this.setTimerSpeed(1);
      }
    },
    adjustTimer(minuteDelta) {
      const deltaSeconds = minuteDelta * 60
      this.totalSeconds += deltaSeconds

      if (!this.countUpMode && this.totalSeconds < 0) {
        this.totalSeconds = 0
      }

      this.updateTimeDisplay()

      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        let message = JSON.stringify({
          action: 'sendclue',
          gameId: this.gameId,
          data: this.hours + ':' + this.minutes + ':' + this.seconds,
          type: 'adjust_timer',
          clue_id: '',
          count_as_hint: 0
        })
        this.socket.send(message)
      }
    },
    setTimerSpeed(speed) {
      this.timerSpeed = speed

      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        let message = JSON.stringify({
          action: 'sendclue',
          gameId: this.gameId,
          data: speed,
          type: 'timer_speed',
          clue_id: '',
          count_as_hint: 0
        })
        this.socket.send(message)
      }
    },
    updateTimeDisplay() {
      let isNegative = this.totalSeconds < 0;
      let absSeconds = Math.abs(this.totalSeconds);

      let h = Math.floor(absSeconds / 3600);
      let m = Math.floor((absSeconds % 3600) / 60);
      let s = absSeconds % 60;

      this.hours = (isNegative ? "-" : "") + String(h).padStart(2, "0");
      this.minutes = String(m).padStart(2, "0");
      this.seconds = String(s).padStart(2, "0");
    },
    handleScroll(event) {
      this.isScrolled = event.target.scrollTop > 10;
    },
    onSolveTimeInput(puzzle, value) {
      // Typed or pasted times arrive dated January 1; see mergePickedSolveTime.
      puzzle.solve_time = mergePickedSolveTime(puzzle.solve_time, value)
      this.validateTime(puzzle)
    },
    validateTime(puzzle){
      if (!puzzle.solve_time) {
        puzzle.solve_time_label = ''
        return
      }
      puzzle.error = null
      // Preserve the date carried by the picked value; do not re-anchor to today,
      // otherwise editing a time after midnight would move the solve to the wrong day.
      puzzle.solve_time_label = this.formatSolveTime(puzzle.solve_time)
    },
    expandImage(image){
      this.selectedImage = image
      this.modals.image = true
    },
    convertTime() {
      for (let n in this.model.puzzles) {
        if(this.model.puzzles[n].solve_time != null
          && this.model.puzzles[n].solve_time && this.model.puzzles[n].solve_time != ''
          && !this.model.puzzles[n].did_not_finish){

          // solve_time should already be a Date object (from TimePicker or our parsing)
          let timeObj = this.model.puzzles[n].solve_time;

          // If it's somehow still a string, convert it
          if(typeof timeObj === 'string') {
            timeObj = new Date(timeObj);
          }

          // Ensure it's a valid Date object
          if(!(timeObj instanceof Date) || isNaN(timeObj.getTime())) {
            continue; // Skip invalid dates
          }

          // Extract hours and minutes from the Date object (local time)
          const hours = timeObj.getHours();
          const minutes = timeObj.getMinutes();

          // Format as MySQL datetime string, preserving the date the puzzle was
          // actually solved. Do NOT use the current date: submitting a tracker
          // after midnight must not move solve times onto the next day.
          this.model.puzzles[n].solve_time = timeObj.getFullYear() + '-'
            + ('0' + (timeObj.getMonth()+1)).slice(-2) + '-'
            + ('0' + timeObj.getDate()).slice(-2) +
            ' ' + ('0' + hours).slice(-2) + ':'
            + ('0' + minutes).slice(-2)
        }

        if (this.model.puzzles[n].did_not_finish == true) {
          this.model.puzzles[n].solve_time = ''
          this.model.puzzles[n].error = null
        }
      }
    },
    changeValidation(row) {
      //test2
      if (row.did_not_finish == true) {
        row.modelValidations = {
          booking_hour: {
            required: false,
            numeric: true,
            max_value: 12
          },
          booking_minute: {
            required: false,
            numeric: true,
            max_value: 59
          },
          hints: {
            required: false,
            min_value: 0
          }
        }

      } else if (row.did_not_finish == false) {
        row.modelValidations = {
          booking_hour: {
            required: true,
            numeric: true,
            max_value: 12
          },
          booking_minute: {
            required: true,
            numeric: true,
            max_value: 59
          },
          hints: {
            required: true,
            min_value: 0
          }
        }
      }
      this.setDraft()
    },
    getPuzzles() {
      const puzzles = JSON.parse(localStorage.getItem(this.draftKey('puzzles')))

      // Convert solve_time strings to Date objects for TimePicker compatibility
      if(puzzles && Array.isArray(puzzles)) {
        puzzles.forEach(puzzle => {
          puzzle.accordionOpen = false
          puzzle.hintPanelOpen = false
          if(puzzle.solve_time && typeof puzzle.solve_time === 'string') {
            // Parse time strings WITHOUT timezone conversion
            // We want to extract the datetime values as-is, not convert from UTC
            const timeStr = puzzle.solve_time;
            let dateParts, timeParts;

            if(timeStr.includes('T')) {
              // ISO format "2025-11-21T19:16:00.000Z" - extract datetime WITHOUT timezone conversion
              const isoTimePart = timeStr.split('T')[0]; // "2025-11-21"
              const isoDateTimePart = timeStr.split('T')[1].split('.')[0]; // "19:16:00" (remove .000Z)

              dateParts = isoTimePart.split('-'); // [2025, 11, 21]
              timeParts = isoDateTimePart.split(':'); // [19, 16, 00]
            } else {
              // MySQL format "2025-11-21 19:16:00"
              const parts = timeStr.split(' ');
              if(parts.length >= 2) {
                dateParts = parts[0].split('-'); // [2025, 11, 21]
                timeParts = parts[1].split(':'); // [19, 16, 00]
              }
            }

            // Create Date in local timezone using the extracted values AS-IS (no UTC conversion)
            if(dateParts && timeParts) {
              const dateObj = new Date(
                parseInt(dateParts[0]), // year
                parseInt(dateParts[1]) - 1, // month (0-indexed)
                parseInt(dateParts[2]), // day
                parseInt(timeParts[0]), // hours - use the value from string as-is
                parseInt(timeParts[1]), // minutes - use the value from string as-is
                parseInt(timeParts[2] || 0) // seconds
              );

              // Only update if valid date
              if(!isNaN(dateObj.getTime())) {
                puzzle.solve_time = dateObj;
              }
            }
          }
        });
      }

      this.model.puzzles = puzzles
    },
    async setDraft(extra = {}) {

      localStorage.setItem(this.draftKey('puzzles'), JSON.stringify(this.model.puzzles))
      //save draft
      let companyId = localStorage.getItem('companyId')
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + companyId
        + '/games/instances/' + this.gameInstanceId + '/drafts'

      this.convertTime()

      let data = {
        timer_value: this.hours + ':' + this.minutes + ':' + this.seconds,
        puzzles: this.model.puzzles,
        ...extra
      }
      return new Promise(function (resolve) {
        axios.put(url, data, config)
          .then(response => {
            resolve(true)
          })
          .catch(err => {
            if (err.response && err.response.status == 401) {
              swal({
                title: 'Error',
                html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                type: 'error',
              }).then((response)=> {
                this.$router.push('/')
              })
              resolve(false)
            } else if (err.response && err.response.status == 409) {
              // The instance is no longer an open draft. This happens when a
              // tracker tab left over from an earlier session is still posting
              // objectives into an event form that has since been submitted.
              swal({
                title: 'This event form has already been submitted',
                text: "Objectives can no longer be recorded here. Open the event from event history to make changes, or start a fresh tracker for the event you are running.",
                type: 'error',
                confirmButtonColor: '#1d8cf8',
                confirmButtonText: 'OK'
              })
              resolve(false)
            } else if (err.response && err.response.status == 500) {
              swal({
                title: 'This booking slot is already attached to another draft',
                text: "Delete the draft or select another booking slot",
                type: 'error',
                confirmButtonColor: '#1d8cf8',
                confirmButtonText: 'OK'
              })
              resolve(false)
            } else {
              // Every branch must settle the promise. Callers await setDraft()
              // before advancing the wizard, so an unhandled status would leave
              // it pending forever and freeze navigation.
              resolve(false)
            }
          })
      })
    },
    initPacing(startDate, durationMinutes) {
      this.pacingStart = startDate instanceof Date && !isNaN(startDate.getTime()) ? startDate : null
      this.pacingLimitSeconds = (Number(durationMinutes) || 0) * 60
      let baseline = null
      if (Array.isArray(this.pacingRows) && this.pacingRows.length > 0) {
        baseline = buildBaseline(this.pacingRows)
      }
      if (!baseline || !baseline.enoughHistory) {
        baseline = evenSpreadBaseline(this.model.puzzles, this.pacingLimitSeconds, baseline ? baseline.sampleSize : 0)
      }
      this.pacingBaseline = baseline
      clearInterval(this.pacingTimerId)
      this.pacingNow = Date.now()
      this.pacingTimerId = setInterval(() => { this.pacingNow = Date.now() }, TICK_MS)
    },
    getPacingContext() {
      if (!this.pacingEnabled || !this.pacingBaseline) {
        return null
      }
      return { baseline: this.pacingBaseline, startDate: this.pacingStart, limitSeconds: this.pacingLimitSeconds }
    },
    pacingChip(puzzle) {
      if (!this.pacing) {
        return null
      }
      const entry = this.pacing.perPuzzle[puzzle.source_puzzle_id]
      return entry && entry.band ? entry : null
    },
    pacingChipLabel(chip) {
      if (chip.delta === null || chip.band === 'overdue') {
        return 'Usually solved by ' + formatDuration(chip.typical)
      }
      return formatDelta(chip.delta)
    },
    setNow(row) {
      row.did_not_finish = false
      row.error = null
      row.solve_time = new Date()
      row.solve_time_label = this.formatSolveTime(row.solve_time)
      this.setDraft()
    },
    togglePuzzle(index) {
      const puzzle = this.model.puzzles[index]
      const wasOpen = puzzle.accordionOpen
      const openCount = this.model.puzzles.filter(p => p.accordionOpen).length

      if (openCount > 1) {
        // Multiple open: just toggle the clicked one
        this.$set(puzzle, 'accordionOpen', !wasOpen)
        this.allExpanded = false
      } else {
        // Single-accordion: close all others, toggle clicked
        this.model.puzzles.forEach(p => {
          this.$set(p, 'accordionOpen', false)
        })
        if (!wasOpen) {
          const done = (puzzle.solve_time && !puzzle.did_not_finish) || puzzle.did_not_finish
          this.$set(puzzle, 'sortAsDone', done)
          this.$set(puzzle, 'accordionOpen', true)
        }
      }
    },
    quickComplete(puzzle) {
      if (puzzle.solve_time && !puzzle.did_not_finish) {
        puzzle.solve_time = null
        puzzle.solve_time_label = ''
        this.setDraft()
      } else {
        this.setNow(puzzle)
      }
    },
    quickDnf(puzzle) {
      puzzle.did_not_finish = !puzzle.did_not_finish
      if (puzzle.did_not_finish) {
        puzzle.solve_time = null
        puzzle.solve_time_label = ''
        puzzle.error = null
      }
      setTimeout(() => {
        this.changeValidation(puzzle)
      }, 500)
    },
    toggleHintPanel(flatIndex) {
      const puzzle = this.model.puzzles[flatIndex]
      this.$set(puzzle, 'hintPanelOpen', !puzzle.hintPanelOpen)
    },
    toggleAllPuzzles() {
      this.allExpanded = !this.allExpanded
      this.model.puzzles.forEach(p => {
        if (this.allExpanded) {
          const done = (p.solve_time && !p.did_not_finish) || p.did_not_finish
          this.$set(p, 'sortAsDone', done)
        }
        this.$set(p, 'accordionOpen', this.allExpanded)
      })
    },
    handlePuzzleHintSent(puzzle, hintData) {
      // Send WebSocket message
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        let data = hintData.content
        let type = hintData.type

        // For video, clear first then send with delay (matching Clues.vue pattern)
        if (type === 'video') {
          let clearMsg = JSON.stringify({
            action: 'sendclue',
            gameId: this.gameId,
            data: '',
            type: 'clear',
            clue_id: ''
          })
          this.socket.send(clearMsg)

          setTimeout(() => {
            let message = JSON.stringify({
              action: 'sendclue',
              gameId: this.gameId,
              data: data,
              type: type,
              clue_id: hintData.clueId || '',
              count_as_hint: hintData.countAsHint || 0,
              loop: hintData.loop || 0
            })
            this.socket.send(message)
          }, 500)
        } else {
          let message = JSON.stringify({
            action: 'sendclue',
            gameId: this.gameId,
            data: data,
            type: type,
            clue_id: hintData.clueId || '',
            count_as_hint: hintData.countAsHint || 0
          })
          this.socket.send(message)
        }
      }

      // Auto-increment hint tracking on puzzle
      this.incrementHintCount(puzzle, hintData)

      // Notify parent (for display panel counter update)
      this.$emit('hint-sent', {
        ...hintData,
        puzzleId: puzzle.puzzle_id
      })
    },
    incrementHintCount(puzzle, hintData) {
      // Increment hint count
      puzzle.hints_given_count = (parseInt(puzzle.hints_given_count) || 0) + 1

      // Append timestamped description
      let timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      let description = hintData.type === 'text' || hintData.type === 'custom'
        ? hintData.content
        : hintData.content.split('/').pop()
      let prefix = puzzle.hints_given_description ? '\n' : ''
      puzzle.hints_given_description = (puzzle.hints_given_description || '') + prefix + timestamp + ': ' + description

      // Track session count for badge
      puzzle.hintsSentThisSession = (puzzle.hintsSentThisSession || 0) + 1

      // Save draft
      this.setDraft()
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async createPuzzles() {
      let companyId = localStorage.getItem('companyId')
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + companyId
        + '/games/instances/' + this.gameInstanceId + '/drafts/puzzles'

      let data = {}
      return axios.post(url, data, config)
        .then(response => {
          const payload = Array.isArray(response.data) ? { puzzles: response.data, pacing: null } : response.data
          this.pacingRows = payload.pacing && Array.isArray(payload.pacing.rows) ? payload.pacing.rows : null
          this.model.puzzles = []
          for (let i in payload.puzzles) {
            // Convert solve_time from MySQL string format to Date object for TimePicker
            let solveTime = null;
            if(payload.puzzles[i].solve_time) {
              if(typeof payload.puzzles[i].solve_time === 'string') {
                // Parse time strings WITHOUT timezone conversion
                // We want to extract the datetime values as-is, not convert from UTC
                const timeStr = payload.puzzles[i].solve_time;
                let dateParts, timeParts;

                if(timeStr.includes('T')) {
                  // ISO format "2025-11-21T19:16:00.000Z" - extract datetime WITHOUT timezone conversion
                  const isoTimePart = timeStr.split('T')[0]; // "2025-11-21"
                  const isoDateTimePart = timeStr.split('T')[1].split('.')[0]; // "19:16:00" (remove .000Z)

                  dateParts = isoTimePart.split('-'); // [2025, 11, 21]
                  timeParts = isoDateTimePart.split(':'); // [19, 16, 00]
                } else {
                  // MySQL format "2025-11-21 19:16:00"
                  const parts = timeStr.split(' ');
                  if(parts.length >= 2) {
                    dateParts = parts[0].split('-'); // [2025, 11, 21]
                    timeParts = parts[1].split(':'); // [19, 16, 00]
                  }
                }

                // Create Date in local timezone using the extracted values AS-IS (no UTC conversion)
                if(dateParts && timeParts) {
                  solveTime = new Date(
                    parseInt(dateParts[0]), // year
                    parseInt(dateParts[1]) - 1, // month (0-indexed)
                    parseInt(dateParts[2]), // day
                    parseInt(timeParts[0]), // hours - use the value from string as-is
                    parseInt(timeParts[1]), // minutes - use the value from string as-is
                    parseInt(timeParts[2] || 0) // seconds
                  );

                  // Validate the Date
                  if(isNaN(solveTime.getTime())) {
                    solveTime = null;
                  }
                } else {
                  solveTime = null;
                }
              } else {
                // Already a Date object or something else
                solveTime = payload.puzzles[i].solve_time;
              }
            }

            this.model.puzzles.push({
              id: payload.puzzles[i].id,
              puzzle_id: payload.puzzles[i].id,
              source_puzzle_id: payload.puzzles[i].puzzle_id || payload.puzzles[i].id,
              parent_id: payload.puzzles[i].parent_id || null,
              name: payload.puzzles[i].name,
              description: payload.puzzles[i].description,
              solution: payload.puzzles[i].solution,
              solution_img: payload.puzzles[i].solution_img,
              position: payload.puzzles[i].position,
              solve_time: solveTime,
              solve_time_label: solveTime ? this.formatSolveTime(solveTime) : '',
              game_id: payload.puzzles[i].game_id,
              error: null,
              hints_given_count: payload.puzzles[i].hints_given_count,
              did_not_finish: Boolean(!payload.puzzles[i].is_completed),
              hints_given_description: payload.puzzles[i].hints_given_description,
              breakage_occurred: Boolean(payload.puzzles[i].breakage_occurred),
              breakage_description: payload.puzzles[i].breakage_description,
              accordionOpen: false,
              hintPanelOpen: false,
              hintsSentThisSession: 0,
              modelValidations: {
                booking_hour: {
                  required: true,
                  numeric: true,
                  max_value: 12
                },
                booking_minute: {
                  required: true,
                  numeric: true,
                  max_value: 59
                },
                hints: {
                  required: true,
                  min_value: 0

                }
              }
            })
          }
          this.convertTime()
          this.model.puzzles.sort((a, b) => (a.position - b.position) || (a.source_puzzle_id - b.source_puzzle_id))

        })
        .catch(err => {
          if (err.response && err.response.status == 401) {
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    checkMissingData() {
      // Find first puzzle with missing solve time (where did_not_finish is false)
      for (let i = 0; i < this.model.puzzles.length; i++) {
        const puzzle = this.model.puzzles[i];

        // Check if puzzle needs data (not marked as "did not finish")
        if (!puzzle.did_not_finish) {
          // Check for missing solve time
          if (!puzzle.solve_time || puzzle.solve_time === '') {
            this.scrollToPuzzle(i, puzzle);
            return;
          }
        }
      }

      // All data is complete
      this.$notify({
        type: 'success',
        message: 'All objective data is complete!',
        icon: 'tim-icons icon-check-2'
      });
    },
    scrollToPuzzle(index, puzzle) {
      // Set error state to highlight the field
      puzzle.error = 'Time is required';

      // Expand the puzzle with missing data
      this.model.puzzles.forEach((p, i) => {
        this.$set(p, 'accordionOpen', i === index)
      })

      // Scroll to the puzzle after DOM updates
      this.$nextTick(() => {
        setTimeout(() => {
          const targetItem = this.$el.querySelector(`[data-flat-index="${index}"]`);
          if (targetItem) {
            targetItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
            targetItem.classList.add('highlight-flash');
            setTimeout(() => {
              targetItem.classList.remove('highlight-flash');
            }, 3000);
          }
        }, 100);
      });
    },
    async completeGame() {
      if (!this.showCompleteButton) return;

      // Stop timer
      this.stopTimer();

      // Check for missing data
      const missingPuzzles = this.model.puzzles.filter(
        p => !p.did_not_finish && (!p.solve_time || p.solve_time === '')
      );

      if (missingPuzzles.length > 0) {
        // Show confirmation dialog
        const result = await swal({
          title: 'Missing objective data',
          text: `${missingPuzzles.length} objective(s) don't have solve times. Mark them as "Did not finish" and continue?`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, complete event',
          cancelButtonText: 'Go back and fix'
        });

        if (!result.value) {
          return;
        }

        // Auto-mark incomplete puzzles as "did not finish"
        missingPuzzles.forEach(p => {
          p.did_not_finish = true;
          p.error = null;
        });
      }

      // Save draft state
      await this.setDraft();

      // Capture the timestamp now
      const endTime = new Date();

      // Emit event with current timestamp for end time
      this.$emit('complete-game', endTime);
    },
    validate() {
      let errors = 0
      for (let n in this.model.puzzles) {
        //validation
        if (( this.model.puzzles[n].solve_time === null || this.model.puzzles[n].solve_time === '')
          && this.model.puzzles[n].did_not_finish == false) {
          this.model.puzzles[n].error = "Time cannot be empty"
          this.model.puzzles[n].solve_time = ''
        } else {
          this.model.puzzles[n].error = null
        }
        if (this.model.puzzles[n].error != null && this.model.puzzles[n].did_not_finish == false) {
          errors++
        }
      }
      if (errors > 0) {
        swal({
          title: 'Ooops!',
          text: 'You have empty or invalid time fields. Do you want to mark them as not finished?',
          type: 'warning',
          confirmButtonText: 'Mark as not finished',
          cancelButtonColor: '#fd5d93',
          cancelButtonText: 'Fix errors',
          showCancelButton: true,
          confirmButtonColor: '#1d8cf8',
        }).then((response) => {
          if (response.value) {
            for (var n in this.model.puzzles) {
              //validation
              if (( this.model.puzzles[n].solve_time === null || this.model.puzzles[n].solve_time === '')
                && this.model.puzzles[n].did_not_finish == false) {
                this.model.puzzles[n].error = null
                this.model.puzzles[n].did_not_finish = true
              }
            }
            return true
          } else {
            return false
          }
        })
      } else {
        return true
      }
    }
  },
  beforeDestroy() {
    this.stopTimer(); // Clean up when component is destroyed
    clearInterval(this.pacingTimerId);
    window.removeEventListener('resize', this.checkScrollableContent);
  },
  mounted() {
    this.resetTimer();
    if (this.time_format === 'HH:mm') {
      this.picker_time_format = 'HH:mm'
    }
    this.$nextTick(() => {
      this.checkScrollableContent();
    });
    window.addEventListener('resize', this.checkScrollableContent);
  }
}
</script>

<style scoped>
/* ========================================
   Layout
   ======================================== */
.sc-container {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  padding: 10px;
  scrollbar-width: none;
}

.sticky-header {
  position: sticky;
  top: -10px;
  background: #222a42 !important;
  padding: 2px 10px 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  z-index: 10;
  transition: box-shadow 0.3s ease;
}

.sticky-header.shadow {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.sticky-header__info {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px 24px;
  max-width: 100%;
}

.sc-content {
  padding-top: 12px;
}

/* ========================================
   Accordion
   ======================================== */
.puzzle-accordion-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-left: 3px solid transparent;
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.5),
    0 1px 3px rgba(0, 0, 0, 0.3);
}

.puzzle-accordion-item:hover {
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.6),
    0 1px 3px rgba(0, 0, 0, 0.3);
}

.puzzle-accordion-item--open {
  border-color: rgba(225, 78, 202, 0.2);
  border-left-color: #e14eca;
  background: rgba(225, 78, 202, 0.06);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.55),
    0 0 6px rgba(225, 78, 202, 0.12),
    inset 0 1px 0 rgba(225, 78, 202, 0.04);
}

.puzzle-accordion-item--open:hover {
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.6),
    0 0 10px rgba(225, 78, 202, 0.18),
    inset 0 1px 0 rgba(225, 78, 202, 0.06);
}

.puzzle-accordion-item--solved {
  border-left-color: #00bf9a;
  background: rgba(0, 191, 154, 0.06);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.55),
    0 0 6px rgba(0, 191, 154, 0.12),
    inset 0 1px 0 rgba(0, 191, 154, 0.04);
  animation: glow-pulse-green 0.6s ease-out;
}

.puzzle-accordion-item--solved:hover {
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.6),
    0 0 10px rgba(0, 191, 154, 0.18),
    inset 0 1px 0 rgba(0, 191, 154, 0.06);
}

.puzzle-accordion-item--dnf {
  border-left-color: #ff8d72;
  background: rgba(255, 141, 114, 0.06);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.55),
    0 0 6px rgba(255, 141, 114, 0.12),
    inset 0 1px 0 rgba(255, 141, 114, 0.04);
  animation: glow-pulse-orange 0.6s ease-out;
}

.puzzle-accordion-item--dnf:hover {
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.6),
    0 0 10px rgba(255, 141, 114, 0.18),
    inset 0 1px 0 rgba(255, 141, 114, 0.06);
}

.puzzle-accordion-item--child {
  margin-left: 32px;
  border-left-color: rgba(225, 78, 202, 0.2);
}

.puzzle-accordion-item--child .puzzle-number {
  font-size: 11px;
}

.puzzle-accordion-item--child.puzzle-accordion-item--solved {
  border-left-color: rgba(0, 191, 154, 0.5);
}

.puzzle-accordion-item--child.puzzle-accordion-item--dnf {
  border-left-color: rgba(255, 141, 114, 0.5);
}

.puzzle-accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
  gap: 12px;
}

.puzzle-accordion-header:hover {
  background: rgba(255, 255, 255, 0.03);
}

.puzzle-accordion-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.puzzle-accordion-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.puzzle-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(225, 78, 202, 0.15);
  color: #e14eca;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(225, 78, 202, 0.3), 0 0 16px rgba(225, 78, 202, 0.1);
  transition: box-shadow 0.3s ease;
}

.puzzle-accordion-item--solved .puzzle-number {
  background: rgba(0, 191, 154, 0.15);
  color: #00bf9a;
  box-shadow: 0 0 8px rgba(0, 191, 154, 0.3), 0 0 16px rgba(0, 191, 154, 0.1);
}

.puzzle-accordion-item--dnf .puzzle-number {
  background: rgba(255, 141, 114, 0.15);
  color: #ff8d72;
  box-shadow: 0 0 8px rgba(255, 141, 114, 0.3), 0 0 16px rgba(255, 141, 114, 0.1);
}

.puzzle-name {
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.puzzle-accordion-chevron {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.2s ease;
  margin-left: 4px;
}

.puzzle-accordion-item--open .puzzle-accordion-chevron {
  color: #e14eca;
}

/* ========================================
   Quick actions
   ======================================== */
.puzzle-quick-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
}

.puzzle-quick-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0 8px;
  white-space: nowrap;
}

.puzzle-quick-btn .tim-icons {
  font-size: 11px;
}

.puzzle-quick-btn--expand {
  padding: 0 6px;
}

.puzzle-quick-btn:hover {
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.puzzle-quick-btn--complete.active {
  border-color: #00bf9a;
  color: #00bf9a;
  background: rgba(0, 191, 154, 0.1);
}

.puzzle-quick-btn--complete:hover {
  border-color: #00bf9a;
  color: #00bf9a;
}

.puzzle-quick-btn--dnf.active {
  border-color: #ff8d72;
  color: #ff8d72;
  background: rgba(255, 141, 114, 0.1);
}

.puzzle-quick-btn--dnf:hover {
  border-color: #ff8d72;
  color: #ff8d72;
}

.puzzle-quick-btn--send.active {
  border-color: #e14eca;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}

.puzzle-quick-btn--send:hover {
  border-color: #e14eca;
  color: #e14eca;
}

.puzzle-hint-quick-section {
  padding: 8px 16px 8px 56px;
}

/* ========================================
   Accordion body
   ======================================== */
.puzzle-accordion-body {
  padding: 0 16px 16px 56px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

/* ========================================
   Badges
   ======================================== */
.puzzle-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.puzzle-badge--solved {
  background: rgba(0, 191, 154, 0.15);
  color: #00bf9a;
}

.puzzle-badge--dnf {
  background: rgba(255, 141, 114, 0.15);
  color: #ff8d72;
}

.puzzle-badge--hints {
  background: rgba(225, 78, 202, 0.12);
  color: #e14eca;
  box-shadow: 0 0 6px rgba(225, 78, 202, 0.15);
}

.puzzle-badge--breakage {
  background: rgba(253, 93, 147, 0.15);
  color: #fd5d93;
  box-shadow: 0 0 6px rgba(253, 93, 147, 0.15);
}

.puzzle-badge--sent {
  background: rgba(29, 140, 248, 0.15);
  color: #1d8cf8;
  box-shadow: 0 0 6px rgba(29, 140, 248, 0.15);
}

.puzzle-badge--pace-fast {
  background: rgba(0, 191, 154, 0.15);
  color: #00bf9a;
}

.puzzle-badge--pace-slow {
  background: rgba(255, 141, 114, 0.15);
  color: #ff8d72;
}

.puzzle-badge--pace-typical {
  background: rgba(125, 125, 125, 0.15);
  color: rgba(255, 255, 255, 0.7);
}

.puzzle-badge--pace-upcoming {
  background: rgba(125, 125, 125, 0.12);
  color: rgba(255, 255, 255, 0.5);
}

.puzzle-badge--pace-approaching {
  background: rgba(255, 178, 54, 0.15);
  color: #ffb236;
}

.puzzle-badge--pace-approaching i {
  font-size: 11px;
}

.puzzle-badge--pace-overdue {
  background: rgba(255, 141, 114, 0.18);
  color: #ff8d72;
  box-shadow: 0 0 8px rgba(255, 141, 114, 0.25);
  animation: pace-overdue-pulse 2.4s ease-in-out infinite;
}

.puzzle-badge--pace-overdue i {
  font-size: 11px;
}

.puzzle-accordion-item--overdue:not(.puzzle-accordion-item--open) {
  border-left-color: #ff8d72;
}

@keyframes pace-overdue-pulse {
  0%, 100% { box-shadow: 0 0 6px rgba(255, 141, 114, 0.2); }
  50% { box-shadow: 0 0 12px rgba(255, 141, 114, 0.45); }
}

/* ========================================
   Info strip
   ======================================== */
.puzzle-info-strip {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(29, 140, 248, 0.06);
  border: 1px solid rgba(29, 140, 248, 0.12);
  border-radius: 4px;
  margin: 12px 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
}

.puzzle-info-strip__text {
  flex: 1;
  min-width: 0;
}

.puzzle-info-strip__line {
  margin: 0 0 4px 0;
}

.puzzle-info-strip__line:last-child {
  margin-bottom: 0;
}

.puzzle-info-strip__label {
  color: #1d8cf8;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 8px;
}

.puzzle-info-strip__img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: border-color 0.2s ease;
}

.puzzle-info-strip__img:hover {
  border-color: #1d8cf8;
}

/* ========================================
   Fields row (horizontal layout)
   ======================================== */
.puzzle-fields-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding-top: 12px;
}

/* ========================================
   Field groups
   ======================================== */
.puzzle-field-group {
  padding: 0 20px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.puzzle-field-group:first-child {
  padding-left: 0;
}

.puzzle-field-group:last-child {
  border-right: none;
  padding-right: 0;
}

.puzzle-field-group--time {
  flex: 0 0 auto;
}

.puzzle-field-group--interactions {
  flex: 1;
  min-width: 220px;
}

.puzzle-field-group--issues {
  flex: 1;
  min-width: 200px;
}

.puzzle-field-label {
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 4px;
  display: block;
}

.puzzle-field-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: nowrap;
}

.puzzle-field--time {
  flex: 0 0 auto;
  min-width: 180px;
}

.puzzle-time-group {
  display: flex;
  align-items: stretch;
}

.puzzle-time-error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #ec250d;
}

.puzzle-time-group .puzzle-time-picker {
  flex: 1;
}


/* Now-append — visual base from gt-now-append */
.puzzle-time-group.is-disabled .puzzle-now-append {
  opacity: 0.4;
  cursor: not-allowed;
}

.puzzle-dnf-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.puzzle-dnf-label {
  font-size: 12px;
  color: #9a9a9a;
  margin-bottom: 0;
  white-space: nowrap;
}

.puzzle-field--hint-count {
  flex: 0 0 auto;
  min-height: 40px;
  display: flex;
  align-items: flex-start;
}

.puzzle-field--desc {
  flex: 1;
  min-width: 180px;
}

.puzzle-field--breakage-check {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 40px;
}

.puzzle-field--desc {
  margin-top: 10px;
  width: 100%;
}

.puzzle-field--desc .puzzle-field-label {
  margin-bottom: 3px;
}

/* ========================================
   Textareas
   ======================================== */
.puzzle-textarea {
  font-size: 12px;
  line-height: 1.5;
  padding: 6px 10px;
  resize: vertical;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.2s ease;
}

.puzzle-textarea:focus {
  border-color: #e14eca;
  background: rgba(255, 255, 255, 0.06);
}

.puzzle-textarea::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

/* Glow-pulse animations now in game-tracker.css */

/* ========================================
   List move transition
   ======================================== */
.puzzle-list-move {
  transition: transform 0.45s ease;
}

/* ========================================
   Highlight flash
   ======================================== */
@keyframes flash-highlight {
  0%, 50% {
    box-shadow: inset 0 0 0 2px rgba(225, 78, 202, 0.5);
  }
  100% {
    box-shadow: inset 0 0 0 2px transparent;
  }
}

.highlight-flash {
  animation: flash-highlight 1s ease-in-out 3;
}

/* ========================================
   Action buttons
   ======================================== */
.utility-btn {
  color: #9a9a9a !important;
  font-size: 13px;
  padding: 0 10px !important;
  margin-bottom: 0 !important;
  text-transform: none !important;
}

.utility-btn:hover {
  color: #ffffff !important;
}

/* ========================================
   Compact GM timer (display mode)
   ======================================== */
.gm-timer-compact {
  display: inline-flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 10px;
}

.gm-timer-compact__controls {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.gm-timer-compact__toggle {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.gm-timer-compact__toggle:hover {
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}

.gm-timer-compact__display {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
}

.gm-timer-compact__time {
  font-family: 'SF Mono', 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 18px;
  font-weight: 300;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.6);
  font-variant-numeric: tabular-nums;
  transition: color 0.3s ease;
}

.gm-timer-compact__display--running .gm-timer-compact__time {
  color: rgba(255, 255, 255, 0.9);
}

.gm-timer-compact__label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 4px;
}

/* ========================================
   Time adjustment buttons
   ======================================== */
.gm-timer-compact__adjust {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: 10px;
  padding-left: 10px;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.gm-adjust-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.3);
  margin: 0 2px;
  user-select: none;
}

.gm-adjust-btn {
  height: 22px;
  min-width: 28px;
  padding: 0 4px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  line-height: 1;
}

.gm-adjust-btn--minus {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.08);
}

.gm-adjust-btn--minus:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
}

.gm-adjust-btn--plus {
  background: rgba(225, 78, 202, 0.1);
  color: rgba(225, 78, 202, 0.8);
  border-color: rgba(225, 78, 202, 0.15);
}

.gm-adjust-btn--plus:hover {
  background: rgba(225, 78, 202, 0.18);
  border-color: rgba(225, 78, 202, 0.3);
  color: #e14eca;
}

/* ========================================
   Timer speed controls
   ======================================== */
.gm-timer-compact__speed {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: 10px;
  padding-left: 10px;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.gm-speed-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.3);
  margin-right: 2px;
  user-select: none;
}

.gm-speed-btn {
  height: 22px;
  min-width: 26px;
  padding: 0 4px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.45);
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.gm-speed-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
}

.gm-speed-btn--active {
  background: rgba(225, 78, 202, 0.15);
  border-color: rgba(225, 78, 202, 0.4);
  color: #e14eca;
  font-weight: 600;
}

/* ========================================
   Responsive
   ======================================== */
@media (max-width: 768px) {
  /* Sticky header: keep single row on mobile */
  .sticky-header .row.align-items-end {
    flex-wrap: nowrap;
  }
  .complete-game-btn { font-size: 13px; padding: 6px 14px !important; }

  .sticky-header__actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  /* Two-row header: name on top, badges + buttons below */
  .puzzle-accordion-header {
    flex-wrap: wrap;
    padding: 10px 12px;
    gap: 6px;
  }

  .puzzle-accordion-header__left {
    width: 100%;
    flex: 1 1 100%;
  }

  .puzzle-accordion-header__right {
    width: 100%;
    flex: 1 1 100%;
    justify-content: flex-end;
  }

  .puzzle-number {
    width: 22px;
    height: 22px;
    font-size: 10px;
  }

  .puzzle-name {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.3;
  }

  /* Quick-action buttons: icon-only */
  .puzzle-quick-btn__label { display: none; }
  .puzzle-quick-btn { padding: 0 6px; }

  /* Reduce child puzzle indent */
  .puzzle-accordion-item--child { margin-left: 16px; }
  .puzzle-hint-quick-section { padding-left: 16px; }

  /* Tighten accordion body padding */
  .puzzle-accordion-body {
    padding: 0 12px 12px 16px;
  }

  .puzzle-fields-row {
    flex-direction: column;
    gap: 12px;
  }

  .puzzle-field-group {
    padding: 0;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding-bottom: 12px;
  }

  .puzzle-field-group:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .puzzle-field-group--interactions,
  .puzzle-field-group--issues {
    min-width: 0;
  }

  .puzzle-field-row {
    flex-wrap: wrap;
    gap: 8px;
  }

  .puzzle-field--hint-count {
    flex: 0 0 auto;
  }

  .puzzle-field--desc {
    flex: 1 1 100%;
  }
}
</style>

<style>
/* Light mode overrides */
.white-content .puzzle-accordion-item {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.08);
  border-left-color: transparent;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.white-content .puzzle-accordion-item:hover {
  background: rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.white-content .puzzle-accordion-item--open {
  border-color: rgba(0, 0, 0, 0.08);
  border-left-color: rgba(225, 78, 202, 0.4);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: rgba(225, 78, 202, 0.04);
  animation: none;
}

.white-content .puzzle-accordion-item--open:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.white-content .puzzle-accordion-item--solved {
  border-left-color: #00bf9a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
  animation: none;
}

.white-content .puzzle-accordion-item--solved:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.white-content .puzzle-accordion-item--dnf {
  border-left-color: #ff8d72;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
  animation: none;
}

.white-content .puzzle-accordion-item--dnf:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.white-content .puzzle-number {
  box-shadow: none;
}

.white-content .puzzle-badge--hints,
.white-content .puzzle-badge--breakage,
.white-content .puzzle-badge--sent {
  box-shadow: none;
}

.white-content .puzzle-badge--pace-typical {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .puzzle-badge--pace-upcoming {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .puzzle-badge--pace-overdue {
  box-shadow: none;
  animation: none;
}

.white-content .puzzle-accordion-item--child {
  border-left-color: rgba(225, 78, 202, 0.25);
}

.white-content .puzzle-accordion-item--child.puzzle-accordion-item--solved {
  border-left-color: rgba(0, 191, 154, 0.6);
}

.white-content .puzzle-accordion-item--child.puzzle-accordion-item--dnf {
  border-left-color: rgba(255, 141, 114, 0.6);
}

.white-content .puzzle-field-group {
  border-right-color: rgba(0, 0, 0, 0.08);
}

.white-content .puzzle-accordion-header:hover {
  background: rgba(0, 0, 0, 0.02);
}

.white-content .puzzle-name {
  color: #344675;
}

.white-content .puzzle-accordion-chevron {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .puzzle-accordion-body {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.white-content .puzzle-field-label {
  color: rgba(0, 0, 0, 0.55);
}

.white-content .puzzle-info-strip {
  background: rgba(29, 140, 248, 0.04);
  border-color: rgba(29, 140, 248, 0.1);
  color: rgba(0, 0, 0, 0.6);
}

.white-content .puzzle-info-strip__img {
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content .sticky-header {
  background: #fff !important;
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

/* Time picker input group overrides (must be unscoped for Element UI internals) */
.puzzle-time-group .puzzle-time-picker .el-date-editor {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.puzzle-time-group .puzzle-time-picker .el-input__inner,
.puzzle-time-group .puzzle-time-picker .form-control {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
}

.puzzle-time-group .el-input.is-disabled .el-input__inner,
.puzzle-time-group .el-input.is-disabled .el-input__inner:disabled {
  background-color: transparent !important;
  color: #9a9a9a !important;
  border-color: rgba(29, 37, 59, 0.5) !important;
}

.white-content .puzzle-time-group .el-input.is-disabled .el-input__inner,
.white-content .puzzle-time-group .el-input.is-disabled .el-input__inner:disabled {
  background-color: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.15) !important;
}

.white-content .utility-btn {
  color: #9a9a9a !important;
}

.white-content .utility-btn:hover {
  color: #344675 !important;
}

.white-content .puzzle-quick-btn {
  border-color: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.4);
}

.white-content .puzzle-quick-btn:hover {
  border-color: rgba(0, 0, 0, 0.3);
  color: #344675;
  background: rgba(0, 0, 0, 0.03);
}

.white-content .puzzle-hint-quick-section {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.white-content .gm-timer-compact__toggle {
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.4);
  background: rgba(0, 0, 0, 0.03);
}

.white-content .gm-timer-compact__toggle:hover {
  border-color: rgba(0, 0, 0, 0.25);
  color: #344675;
}

.white-content .gm-timer-compact__time {
  color: rgba(0, 0, 0, 0.4);
}

.white-content .gm-timer-compact__display--running .gm-timer-compact__time {
  color: #344675;
}

.white-content .gm-timer-compact__label {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .gm-timer-compact__adjust {
  border-left-color: rgba(0, 0, 0, 0.1);
}

.white-content .gm-adjust-label {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .gm-adjust-btn--minus {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.6);
  border-color: rgba(0, 0, 0, 0.08);
}

.white-content .gm-adjust-btn--minus:hover {
  background: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.8);
}

.white-content .gm-timer-compact__speed {
  border-left-color: rgba(0, 0, 0, 0.1);
}

.white-content .gm-speed-label {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .gm-speed-btn {
  border-color: rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
  color: rgba(0, 0, 0, 0.45);
}

.white-content .gm-speed-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
}

/* El-input-number sizing in puzzle fields */
.puzzle-field--hint-count .el-input-number--small {
  width: 120px;
}

.white-content .puzzle-textarea {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.12);
  color: #344675;
}

.white-content .puzzle-textarea:focus {
  border-color: #e14eca;
  background: #fff;
}

.white-content .puzzle-textarea::placeholder {
  color: rgba(0, 0, 0, 0.3);
}
</style>
